<template>
  <section class="practice-select-page">
    <header class="hero-card">
      <button type="button" class="back-btn" @click="goPortal">{{ t("back") }}</button>

      <div class="hero-copy">
        <p class="hero-eyebrow">{{ t("eyebrow") }}</p>
        <h1 class="hero-title">{{ t("title") }}</h1>
        <p class="hero-lead">{{ t("lead") }}</p>
      </div>

      <div class="lang-pill">
        <button
          v-for="choice in langs"
          :key="choice"
          type="button"
          :class="['lang-chip', { active: choice === lang }]"
          @click="setLang(choice)"
        >
          {{ choice.toUpperCase() }}
        </button>
      </div>
    </header>

    <section class="content-grid">
      <article class="panel-card">
        <div class="section-head">
          <span class="panel-tag">{{ t("modeTag") }}</span>
          <h2 class="section-title">{{ t("modeTitle") }}</h2>
          <p class="section-copy">{{ t("modeCopy") }}</p>
        </div>

        <label class="mode-card">
          <input v-model="selectedMode" type="radio" value="options" />
          <div>
            <strong>{{ t("optionMode") }}</strong>
            <p>{{ t("optionModeCopy") }}</p>
          </div>
        </label>

        <label class="mode-card">
          <input v-model="selectedMode" type="radio" value="typing" />
          <div>
            <strong>{{ t("typingMode") }}</strong>
            <p>{{ t("typingModeCopy") }}</p>
          </div>
        </label>

        <label class="mode-card">
          <input v-model="selectedMode" type="radio" value="ai" />
          <div>
            <strong>{{ aiModeTitle() }}</strong>
            <p>{{ aiModeCopy() }}</p>
          </div>
        </label>
      </article>

      <article class="panel-card">
        <div class="section-head">
          <span class="panel-tag">{{ t("filterTag") }}</span>
          <h2 class="section-title">{{ t("filterTitle") }}</h2>
          <p class="section-copy">{{ t("filterCopy") }}</p>
        </div>

        <p v-if="isAiMode" class="mode-note">{{ aiModeNote() }}</p>

        <label class="toggle-row">
          <input v-model="useRootFilter" type="checkbox" />
          <span>{{ t("enableRootFilter") }}</span>
        </label>

        <div class="search-row">
          <input
            v-model.trim="rootKeyword"
            :disabled="!useRootFilter || isAiMode"
            type="text"
            :placeholder="t('rootPlaceholder')"
            @keyup.enter="searchRoots"
          />
          <button type="button" :disabled="!useRootFilter || loadingRoots || isAiMode" @click="searchRoots">
            {{ loadingRoots ? t("searching") : t("search") }}
          </button>
        </div>

        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>

        <div class="root-list">
          <button
            v-for="item in rootRows"
            :key="item.root_id"
            type="button"
            :class="['root-chip', { active: selectedRootId === item.root_id }]"
            @click="selectRoot(item)"
          >
            <strong>{{ item.root_text }}</strong>
            <span>{{ getRootMeaning(item, lang) }}</span>
          </button>
        </div>

        <label class="toggle-row">
          <input v-model="resumeLast" type="checkbox" />
          <span>{{ t("resumeLast") }}</span>
        </label>

        <button type="button" class="confirm-btn" @click="confirmMode">
          {{ t("confirm") }}
        </button>
      </article>
    </section>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useSiteLanguage } from "../../store/siteLanguage";
import { API_BASE, ensureApiArray, getRootMeaning } from "./englishStudyShared";

const router = useRouter();
const STORAGE_KEY = "english_practice_config";
const { lang, languages: langs, setLang } = useSiteLanguage();

const selectedMode = ref("options");
const useRootFilter = ref(false);
const rootKeyword = ref("");
const resumeLast = ref(false);
const selectedRootId = ref("");
const rootRows = ref([]);
const loadingRoots = ref(false);
const errorMessage = ref("");
const isAiMode = computed(() => selectedMode.value === "ai");

const i18n = {
  ja: {
    back: "Back",
    eyebrow: "PRACTICE",
    title: "練習モードを選ぶ",
    lead: "語根で絞るかどうかを決めてから、選択式か入力式かを選べます。",
    modeTag: "MODE",
    modeTitle: "出題形式",
    modeCopy: "今の学習リズムに合うモードを選んでください。",
    optionMode: "選択式",
    optionModeCopy: "意味を見て、正しい構造パーツの意味を選びます。",
    typingMode: "入力式",
    typingModeCopy: "意味を見て、構造パーツと単語を自分で入力します。",
    filterTag: "FILTER",
    filterTitle: "語根フィルター",
    filterCopy: "必要なら一つの語根だけに絞って練習できます。",
    enableRootFilter: "語根で絞る",
    rootPlaceholder: "act / ag / ER00000001",
    searching: "検索中",
    search: "検索",
    resumeLast: "前回の位置から再開する",
    confirm: "この設定で始める",
    searchError: "語根検索に失敗しました。",
  },
  zh: {
    back: "Back",
    eyebrow: "PRACTICE",
    title: "选择练习模式",
    lead: "可以先决定是否按词根筛选，再选择做选择题还是输入题。",
    modeTag: "MODE",
    modeTitle: "出题形式",
    modeCopy: "选择更适合你当前学习节奏的练习方式。",
    optionMode: "选择题",
    optionModeCopy: "看到意义后，从选项里选出正确的构词含义。",
    typingMode: "输入题",
    typingModeCopy: "看到意义后，自己输入结构部分和完整单词。",
    filterTag: "FILTER",
    filterTitle: "词根筛选",
    filterCopy: "如果你想集中复习，也可以只练某一个词根。",
    enableRootFilter: "按词根筛选",
    rootPlaceholder: "act / ag / ER00000001",
    searching: "搜索中",
    search: "搜索",
    resumeLast: "从上次位置继续",
    confirm: "按此设置开始",
    searchError: "词根搜索失败。",
  },
  en: {
    back: "Back",
    eyebrow: "PRACTICE",
    title: "Choose a Practice Mode",
    lead: "Filter by root if needed, then choose multiple-choice or typing practice.",
    modeTag: "MODE",
    modeTitle: "Question Style",
    modeCopy: "Pick the format that fits your current study rhythm.",
    optionMode: "Multiple Choice",
    optionModeCopy: "See a meaning and choose the right structure-part meaning.",
    typingMode: "Typing",
    typingModeCopy: "See the meaning and type the structure parts yourself.",
    filterTag: "FILTER",
    filterTitle: "Root Filter",
    filterCopy: "Limit practice to a specific root when you want focused review.",
    enableRootFilter: "Filter by root",
    rootPlaceholder: "act / ag / ER00000001",
    searching: "Searching",
    search: "Search",
    resumeLast: "Resume last position",
    confirm: "Start with this setup",
    searchError: "Failed to search roots.",
  },
};

function t(key) {
  return i18n[lang.value]?.[key] || key;
}

function aiModeTitle() {
  if (lang.value === "zh") {
    return "AI词根练习";
  }

  if (lang.value === "en") {
    return "AI Root Practice";
  }

  return "AI語根練習";
}

function aiModeCopy() {
  if (lang.value === "zh") {
    return "从 ag 开始，按顺序进行问答式词根练习，每个单词完成前不能切换语言。";
  }

  if (lang.value === "en") {
    return "Start from ag and work through guided AI conversation one word at a time with the language locked per word.";
  }

  return "ag から順番に、会話しながら進める AI 練習です。単語を終えるまでは言語を切り替えません。";
}

function aiModeNote() {
  if (lang.value === "zh") {
    return "AI 模式会固定从 ag 开始，忽略下方的词根筛选。";
  }

  if (lang.value === "en") {
    return "AI mode always starts from ag and ignores the root filter below.";
  }

  return "AI モードは ag から固定で始まり、下の語根フィルターは使いません。";
}

function readSavedConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }

    const cfg = JSON.parse(raw);
    if (cfg.mode === "options" || cfg.mode === "typing" || cfg.mode === "ai") {
      selectedMode.value = cfg.mode;
    }
    useRootFilter.value = !!cfg.useRootFilter;
    rootKeyword.value = cfg.rootKeyword || "";
    selectedRootId.value = cfg.selectedRootId || "";
    resumeLast.value = !!cfg.resumeLast;
  } catch (error) {
    console.error("Failed to read practice config:", error);
  }
}

async function searchRoots() {
  if (!useRootFilter.value || isAiMode.value) {
    return;
  }

  loadingRoots.value = true;
  errorMessage.value = "";
  try {
    const response = await axios.get(`${API_BASE}/api/root/search`, {
      params: rootKeyword.value ? { keyword: rootKeyword.value } : {},
      timeout: 8000,
    });
    rootRows.value = ensureApiArray(response, "Root search API");
  } catch (error) {
    console.error(error);
    rootRows.value = [];
    errorMessage.value = error?.message || t("searchError");
  } finally {
    loadingRoots.value = false;
  }
}

function selectRoot(item) {
  selectedRootId.value = item.root_id;
  rootKeyword.value = item.root_text || "";
}

function confirmMode() {
  const config = {
    mode: selectedMode.value,
    useRootFilter: useRootFilter.value,
    rootKeyword: useRootFilter.value ? rootKeyword.value.trim() : "",
    selectedRootId: useRootFilter.value ? selectedRootId.value : "",
    resumeLast: resumeLast.value,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  localStorage.setItem("english_practice_resume", String(resumeLast.value));
  if (selectedMode.value === "typing") {
    router.push("/english/practice/typing");
    return;
  }

  if (selectedMode.value === "ai") {
    router.push("/english/practice/ai");
    return;
  }

  router.push("/english/practice/options");
}

function goPortal() {
  router.push("/english/englishdectionary");
}

onMounted(() => {
  readSavedConfig();
  if (useRootFilter.value) {
    searchRoots();
  }
});
</script>

<style scoped>
.practice-select-page {
  width: 100%;
  min-height: calc(100vh - 86px);
  padding: 18px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 30%),
    linear-gradient(180deg, #f5f9ff 0%, #eef6fb 100%);
  color: #163047;
}

.hero-card,
.panel-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 50px rgba(34, 73, 112, 0.08);
}

.hero-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 18px;
  padding: 22px 24px;
  align-items: start;
}

.back-btn,
.lang-chip,
.mode-card,
.root-chip,
.confirm-btn,
.search-row button {
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease;
}

.back-btn {
  border: 0;
  border-radius: 16px;
  padding: 12px 16px;
  background: #dceaf8;
  color: #133251;
  font-weight: 700;
}

.hero-eyebrow,
.panel-tag {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #35648e;
}

.hero-title,
.section-title {
  margin: 0;
  color: #10253a;
}

.hero-title {
  font-size: clamp(32px, 5vw, 56px);
  line-height: 0.98;
}

.hero-lead,
.section-copy,
.mode-card p {
  margin: 0;
  color: #4c6377;
  line-height: 1.7;
}

.lang-pill {
  display: inline-flex;
  gap: 8px;
  padding: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(43, 90, 132, 0.14);
}

.lang-chip {
  border: 0;
  min-width: 54px;
  padding: 10px 14px;
  border-radius: 999px;
  background: transparent;
  color: #47647d;
}

.lang-chip.active {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 18px;
}

.panel-card {
  padding: 22px;
}

.section-head {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.mode-card,
.root-chip {
  width: 100%;
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 22px;
  background: #fbfdff;
  padding: 16px;
  text-align: left;
}

.mode-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.toggle-row {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  margin: 8px 0;
}

.search-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  margin-top: 8px;
}

.search-row input {
  width: 100%;
  border: 1px solid rgba(43, 90, 132, 0.18);
  border-radius: 14px;
  background: #fbfdff;
  padding: 12px 14px;
  font-size: 16px;
  color: #12324f;
  box-sizing: border-box;
}

.search-row button,
.confirm-btn {
  border: 0;
  border-radius: 14px;
  padding: 12px 18px;
  font-weight: 700;
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.root-list {
  display: grid;
  gap: 10px;
  margin-top: 14px;
  max-height: 280px;
  overflow: auto;
}

.root-chip.active {
  border-color: rgba(15, 79, 140, 0.28);
  background: #eef6ff;
}

.root-chip strong {
  display: block;
  color: #10253a;
}

.root-chip span {
  display: block;
  margin-top: 8px;
  color: #4c6377;
}

.confirm-btn {
  margin-top: 14px;
}

.error-text {
  color: #b42318;
}

.mode-note {
  margin: 8px 0 0;
  color: #4c6377;
  line-height: 1.6;
}

.back-btn:hover,
.lang-chip:hover,
.mode-card:hover,
.root-chip:hover,
.confirm-btn:hover,
.search-row button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(25, 79, 121, 0.12);
}

@media (max-width: 1100px) {
  .hero-card,
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .practice-select-page {
    padding: 12px;
  }

  .hero-card,
  .panel-card {
    padding: 18px;
    border-radius: 22px;
  }
}
</style>
